import { useEffect, useState } from "react";
import { authHeadersForJson } from "./clientAuth";
import { formatHistoryActorLabel } from "./historyDisplay";

export type HistoryActorRef = {
  userId: string | null;
  kind: "line" | "user" | "system" | "unknown";
  fallbackLabel: string | null;
};

const profileCache = new Map<
  string,
  { displayName: string | null; avatarUrl: string | null }
>();

function readStoredUserId(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem("user");
    if (!raw?.trim()) return null;
    const user = JSON.parse(raw) as { id?: string };
    return typeof user.id === "string" && user.id.trim() ? user.id.trim() : null;
  } catch {
    return null;
  }
}

function readStoredGoogleProfile(): {
  displayName?: string;
  email?: string;
  pictureUrl?: string;
} | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem("google_profile");
    if (!raw?.trim()) return null;
    const profile = JSON.parse(raw) as Record<string, unknown>;
    return {
      displayName:
        typeof profile.displayName === "string" ? profile.displayName : undefined,
      email: typeof profile.email === "string" ? profile.email : undefined,
      pictureUrl:
        typeof profile.pictureUrl === "string" ? profile.pictureUrl : undefined,
    };
  } catch {
    return null;
  }
}

function readStoredUserAvatar(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const googlePicture = readStoredGoogleProfile()?.pictureUrl?.trim();
    if (googlePicture) return googlePicture;
    const raw = window.localStorage.getItem("user");
    if (!raw?.trim()) return null;
    const user = JSON.parse(raw) as Record<string, unknown>;
    const profile = (user.profile as Record<string, unknown> | undefined) ?? {};
    for (const value of [
      user.avatar,
      user.picture,
      user.image,
      profile.avatar,
      profile.pictureUrl,
      profile.picture,
    ]) {
      if (typeof value === "string" && value.trim()) return value.trim();
    }
  } catch {
    // ignore
  }
  return null;
}

function isUsableActorLabel(label: string | null | undefined): label is string {
  const trimmed = label?.trim();
  if (!trimmed || trimmed === "—") return false;
  return formatHistoryActorLabel(trimmed) !== null;
}

function pickActorDisplayName(
  ...candidates: Array<string | null | undefined>
): string | null {
  for (const candidate of candidates) {
    if (isUsableActorLabel(candidate)) return candidate.trim();
  }
  return null;
}

/** Match sign-in / `GET /users/:id` shapes (nested `profile`). */
function extractDisplayNameFromUserRecord(
  user: Record<string, unknown> | null | undefined,
  googleProfile?: ReturnType<typeof readStoredGoogleProfile>,
): string | null {
  if (!user) return null;
  const profile = (user.profile as Record<string, unknown> | undefined) ?? {};
  const firstLast =
    [
      typeof profile.firstName === "string" ? profile.firstName : "",
      typeof profile.lastName === "string" ? profile.lastName : "",
    ]
      .filter(Boolean)
      .join(" ")
      .trim() ||
    [
      typeof user.firstName === "string" ? user.firstName : "",
      typeof user.lastName === "string" ? user.lastName : "",
    ]
      .filter(Boolean)
      .join(" ")
      .trim();

  const fromUser =
    (typeof profile.displayName === "string" && profile.displayName.trim()) ||
    (typeof user.displayName === "string" && user.displayName.trim()) ||
    (typeof user.name === "string" && user.name.trim()) ||
    firstLast ||
    (typeof user.email === "string" && user.email.trim()) ||
    (typeof profile.email === "string" && profile.email.trim()) ||
    null;

  const fromGoogle =
    googleProfile?.displayName?.trim() || googleProfile?.email?.trim() || null;

  return fromUser || fromGoogle || null;
}

function readStoredUserDisplayName(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const googleProfile = readStoredGoogleProfile();
    const raw = window.localStorage.getItem("user");
    const user = raw
      ? (JSON.parse(raw) as Record<string, unknown>)
      : null;
    return extractDisplayNameFromUserRecord(user, googleProfile);
  } catch {
    return null;
  }
}

function extractUserDisplayName(data: unknown): string | null {
  if (!data || typeof data !== "object") return null;
  const root = data as Record<string, unknown>;
  const user =
    (root.user as Record<string, unknown> | undefined) ??
    (root.data as Record<string, unknown> | undefined) ??
    root;
  return extractDisplayNameFromUserRecord(user);
}

function extractUserAvatar(data: unknown): string | null {
  if (!data || typeof data !== "object") return null;
  const root = data as Record<string, unknown>;
  const user =
    (root.user as Record<string, unknown> | undefined) ??
    (root.data as Record<string, unknown> | undefined) ??
    root;
  const profile = (user.profile as Record<string, unknown> | undefined) ?? {};
  for (const value of [
    user.avatar,
    user.picture,
    user.image,
    profile.avatar,
    profile.pictureUrl,
    profile.picture,
  ]) {
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return null;
}

export function parseHistoryActorRef(raw: string): HistoryActorRef {
  const trimmed = (raw || "").trim();
  if (!trimmed || trimmed === "—") {
    return { userId: null, kind: "unknown", fallbackLabel: null };
  }
  if (trimmed === "system" || trimmed === "ระบบ") {
    return { userId: null, kind: "system", fallbackLabel: "ระบบ" };
  }
  if (/^line_/i.test(trimmed)) {
    return { userId: trimmed, kind: "line", fallbackLabel: "LINE" };
  }
  const formatted = formatHistoryActorLabel(trimmed);
  if (formatted) {
    return { userId: trimmed, kind: "user", fallbackLabel: formatted };
  }
  return { userId: trimmed, kind: "user", fallbackLabel: null };
}

export function useHistoryActorProfile(
  actorId: string | null | undefined,
  actorLabel?: string | null,
): { displayName: string | null; avatarUrl: string | null; kind: HistoryActorRef["kind"] } {
  const trimmedActorId = (actorId || "").trim();
  const trimmedActorLabel = actorLabel?.trim() || null;

  const [profile, setProfile] = useState<{
    displayName: string | null;
    avatarUrl: string | null;
    kind: HistoryActorRef["kind"];
  }>(() => {
    const ref = parseHistoryActorRef(trimmedActorId);
    const currentUserId = readStoredUserId();
    const ownName =
      ref.userId && currentUserId === ref.userId
        ? readStoredUserDisplayName()
        : null;
    return {
      displayName: pickActorDisplayName(
        ownName,
        ref.fallbackLabel,
        trimmedActorLabel,
      ),
      avatarUrl: null,
      kind: ref.kind,
    };
  });

  useEffect(() => {
    const ref = parseHistoryActorRef(trimmedActorId);
    if (!ref.userId || ref.kind === "line" || ref.kind === "system") {
      setProfile({
        displayName: pickActorDisplayName(ref.fallbackLabel, trimmedActorLabel),
        avatarUrl: null,
        kind: ref.kind,
      });
      return;
    }

    const cached = profileCache.get(ref.userId);
    if (cached) {
      setProfile({
        displayName: pickActorDisplayName(
          cached.displayName,
          ref.fallbackLabel,
          trimmedActorLabel,
        ),
        avatarUrl: cached.avatarUrl,
        kind: ref.kind,
      });
      return;
    }

    const currentUserId = readStoredUserId();
    if (currentUserId && currentUserId === ref.userId) {
      const ownAvatar = readStoredUserAvatar();
      const ownName = pickActorDisplayName(
        readStoredUserDisplayName(),
        ref.fallbackLabel,
        trimmedActorLabel,
      );
      const next = { displayName: ownName, avatarUrl: ownAvatar };
      profileCache.set(ref.userId, next);
      setProfile({ ...next, kind: ref.kind });
      return;
    }

    const apiBase = process.env.NEXT_PUBLIC_API_BASE_URL?.trim();
    if (!apiBase) {
      setProfile({
        displayName: pickActorDisplayName(ref.fallbackLabel, trimmedActorLabel),
        avatarUrl: null,
        kind: ref.kind,
      });
      return;
    }

    let cancelled = false;
    void fetch(`${apiBase.replace(/\/$/, "")}/users/${encodeURIComponent(ref.userId)}`, {
      headers: {
        Accept: "application/json",
        ...authHeadersForJson(),
      },
    })
      .then(async (res) => {
        if (!res.ok) return null;
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        const next = {
          displayName: pickActorDisplayName(
            extractUserDisplayName(data),
            ref.fallbackLabel,
            trimmedActorLabel,
          ),
          avatarUrl: extractUserAvatar(data),
        };
        profileCache.set(ref.userId!, next);
        setProfile({ ...next, kind: ref.kind });
      })
      .catch(() => {
        if (cancelled) return;
        const next = {
          displayName: pickActorDisplayName(ref.fallbackLabel, trimmedActorLabel),
          avatarUrl: null,
        };
        profileCache.set(ref.userId!, next);
        setProfile({ ...next, kind: ref.kind });
      });

    return () => {
      cancelled = true;
    };
  }, [trimmedActorId, trimmedActorLabel]);

  return profile;
}
